import * as z from 'zod';
import { isoDateTimeSchema, strict, uuidSchema } from './common.js';
/**
 * AD-47: an anomaly is a derived query, an alert is a row.
 *
 * An anomaly needs no table of its own — it is `presence_events` where the result was an override,
 * already durable and already indexed. The `alerts` table exists only for the things that require
 * an acknowledgment and have no natural home: a suspected exfiltration, a break in the audit
 * chain, a failed backup, somebody who went out and has not come back, a passport about to expire.
 *
 * **`detail` carries ids and counts, never personal data.** An alert is rendered on a dashboard,
 * emailed, and kept until somebody acknowledges it, which is three copies outside the encryption
 * boundary; a name in it would be a name in all three.
 *
 * The two lists below are declared here rather than only in the database because both columns are
 * PostgreSQL enums, and a kind the API can raise that the column cannot store is a 500 on the path
 * that exists to report that something is wrong. The schema test compares the member lists.
 */
/** What an alert is about. Each kind has exactly one producer, named in Section 1.9's job list. */
export const ALERT_KINDS = [
    'exfiltration',
    'chain_break',
    'backup_failed',
    /**
     * Somebody went out and has not come back inside within the allowance.
     *
     * Raised per person by the five-minute sweep past `presence.outside_too_long_minutes`, and
     * cleared by that same sweep once they are back. Unlike its five siblings this condition is
     * ORDINARY rather than rare — people go out every day — so it is raised with `raise` and never
     * `raiseOrRefresh`: one row and two audit rows per excursion that exceeded the allowance, not one
     * per evaluation.
     */
    'outside_too_long',
    'passport_expiry',
    /**
     * AD-59: the box of pre-printed bands is running out.
     *
     * Evaluated at boot and then on each claim, each import and each void, rather than on a
     * schedule. The boot evaluation is not redundant: a fresh installation in `preprinted` mode has
     * had no claim, no import and no void, which is exactly the state the alert exists to report.
     */
    'blanks_low',
];
export const alertKindSchema = z.enum(ALERT_KINDS);
/**
 * How loudly it is shown.
 *
 * Three levels rather than five: the only decision a level drives here is whether the dashboard
 * shows it quietly, shows it in the alert bar, or shows it in the alert bar and mails the
 * configured recipients. A scale finer than the decisions it feeds is a scale nobody applies
 * consistently.
 */
export const ALERT_SEVERITIES = ['info', 'warning', 'critical'];
export const alertSeveritySchema = z.enum(ALERT_SEVERITIES);
/**
 * One alert on the wire.
 *
 * `detail` is a flat record of numbers, strings and booleans, and the writer's own grammar is what
 * keeps it that way: `alerts/service.ts` builds it from ids and counts, and AD-47 forbids a name or
 * a value outright, because an alert is rendered on a dashboard, mailed, and kept until it is
 * closed — three copies outside the encryption boundary.
 *
 * `dedupeKey` travels, and that is deliberate: it is the identity of the CONDITION rather than of
 * the row, so a dashboard that wants to group two raises of one condition across an acknowledgment
 * has nothing else to group by. It may embed a subject id, which is why it is admitted here and
 * refused in audit detail, where `redact.ts`'s grammar admits neither `a:b` nor a capitalised
 * segment.
 */
export const alertSchema = strict({
    id: uuidSchema,
    kind: alertKindSchema,
    severity: alertSeveritySchema,
    dedupeKey: z.string().min(1).max(200),
    detail: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])),
    raisedAt: isoDateTimeSchema,
    acknowledgedAt: isoDateTimeSchema.nullable(),
    acknowledgedBy: uuidSchema.nullable(),
    resolvedAt: isoDateTimeSchema.nullable(),
    resolvedBy: uuidSchema.nullable(),
    resolvedReason: z.string().min(1).max(200).nullable(),
});
/**
 * AD-47's dedupe key for an exfiltration alert about one subject: an account or an API key.
 *
 * **Written here, once, because two programs have to agree on it.** The worker's exfiltration job
 * raises under this key, and both the acknowledge route and the browser's alert list ask whether an
 * alert is about the account looking at it. A second copy of the format in either place would be a
 * self-acknowledgement guard that silently stopped matching the day the job's format moved.
 */
export function exfiltrationAlertKey(subjectId) {
    return `exfiltration:${subjectId}`;
}
/**
 * Whether an alert was raised about this account itself.
 *
 * Only an exfiltration alert names an account, so this is the one kind it can answer yes for.
 * **Nobody may mark such an alert as seen**: the account being measured is the one account whose
 * acknowledgement would silence the measurement. The server refuses it and records the attempt;
 * the browser does not offer it.
 */
export function alertConcernsAccount(alert, userId) {
    return alert.dedupeKey === exfiltrationAlertKey(userId);
}
/**
 * Which alerts the dashboard is asking for.
 *
 * The default is AD-47's OPEN set: `acknowledged_at is null and resolved_at is null`, **both
 * clauses**. Dropping the second would keep a cleared alert on the dashboard forever; dropping the
 * first would put an acknowledged one back. `includeAcknowledged` is the deliberate opt-in for the
 * screen that shows what somebody has already seen but the system has not yet closed.
 */
export const alertQuerySchema = strict({
    includeAcknowledged: z
        .union([z.literal('true'), z.literal('false')])
        .default('false')
        .transform((value) => value === 'true'),
});
/** Acknowledging one alert. There is nothing to say, so there is nothing in the body. */
export const acknowledgeAlertRequestSchema = strict({});
/** Every alert the caller asked for, newest raise last, exactly as the service orders them. */
export const alertListSchema = strict({ items: z.array(alertSchema) });
//# sourceMappingURL=alert.js.map